import { TApiError, useAPI } from '@/services';
import { IMelliCardInquiry } from '@/services/apis/fundTransfer/types';
import {
  useAuthenticator,
  useIsAuthenticatorSupported,
} from '@/modules/auth/hooks/Authenticator';
import {
  useHasPasscode,
  useIsPasscodeSupported,
} from '@/modules/auth/hooks/Passcode';
import { useGetUserStatus } from '@/userStatus/hooks';

interface IUseMelliCardIssueStatusReturn {
  shouldShowEditSejam: boolean;
  shouldCreateAccount: boolean;
  isAccountAlreadyCreated: boolean;
  isAccountCreationFailed: boolean;
  error: TApiError | null;
  isLoading: boolean;
  melliCardInquiry: IMelliCardInquiry | undefined;
  isUnhandledError: boolean;
  refetchData: () => void;
  isFetching: boolean;
  shouldUserAuthenticate: () => boolean;
}

interface IUseMelliCardIssueStatusParams {
  isGetCreateAccountStatusEnabled?: boolean;
}

export const useMelliCardIssueStatus = ({
  isGetCreateAccountStatusEnabled,
}: IUseMelliCardIssueStatusParams = {}): IUseMelliCardIssueStatusReturn => {
  const { isVerified } = useGetUserStatus();

  // This service call is used when the user has not yet attempted to create an account.
  const {
    error: createAccountError,
    isSuccess,
    isLoading: isAccountStatusLoading,
    isFetching: isAccountStatusFetching,
    refetch: refetchCreateAccount,
  } = useAPI(['fundTransfer', 'getCreateAccountStatus'], {
    input: {},
    reactQueryOptions: {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: isGetCreateAccountStatusEnabled,
    },
  });

  // This service call is used to get back the status of users account creation process.
  const {
    data: melliCardInquiry,
    isLoading: isAccountInquiryLoading,
    isFetching: isAccountInquiryFetching,
    error: inquiryError,
    refetch: refetchInquiry,
  } = useAPI(['fundTransfer', 'getMelliCardAccountInquiry'], {
    reactQueryOptions: {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: isVerified,
    },
  });

  const { hasPasscode, isLoading: isPassCodeLoading } = useHasPasscode();
  const { hasAuthenticator, isLoading: isAuthenticatorLoading } =
    useAuthenticator();

  const isPasscodeSupported = useIsPasscodeSupported();
  const isAuthenticatorSupported = useIsAuthenticatorSupported();

  const inquiryStatus = melliCardInquiry?.status || '';
  const createErrorCode = createAccountError?.code;
  const inquiryErrorCode = inquiryError?.code;

  // If the account status returns 200, it indicates that the user either does not have an account
  // or does not have an active account creation request.
  const shouldCreateAccount = isSuccess || inquiryStatus === 'InitialRequest';

  const isAccountAlreadyCreated =
    inquiryStatus === 'Successful' ||
    createErrorCode === 'UserHasActiveAccount';

  const isAccountCreationFailed = inquiryStatus === 'Failed';

  const shouldShowEditSejam = [
    'InvalidSejamState',
    'InvalidUserDataOnSejam',
  ].includes(createErrorCode as string);

  const isLoading =
    isAccountStatusLoading ||
    isAccountInquiryLoading ||
    isPassCodeLoading ||
    isAuthenticatorLoading;

  const isFetching = isAccountInquiryFetching || isAccountStatusFetching;

  const isUnhandledError = [inquiryErrorCode, createErrorCode].includes(
    'UnhandledError'
  );

  const error = createAccountError || inquiryError;

  const refetchData = () => {
    if (inquiryErrorCode === 'UnhandledError') refetchInquiry();
    else refetchCreateAccount();
  };

  const shouldUserAuthenticate = () => {
    // If user hasn't created an account, there's no need to force them to authenticate
    if (!isAccountAlreadyCreated) return false;
    // If neither passcode nor authenticator is supported, no authentication onboarding is needed
    if (!isPasscodeSupported && !isAuthenticatorSupported) return false;
    // If passcode is supported and the user has set up a passcode, no authentication onboarding is needed
    if (isPasscodeSupported && hasPasscode) return false;
    // If fingerprint authentication is supported and the user has set it up, no authentication onboarding is needed
    // If not, the user needs to activate an authentication method
    return !(isAuthenticatorSupported && hasAuthenticator);
  };

  return {
    shouldShowEditSejam,
    shouldCreateAccount,
    isAccountAlreadyCreated,
    isAccountCreationFailed,
    error,
    isLoading,
    melliCardInquiry,
    isUnhandledError,
    refetchData,
    isFetching,
    shouldUserAuthenticate,
  };
};
